import * as dateFns from 'date-fns';
import ptBR from 'date-fns/locale/pt';
import React, { useEffect, useMemo, useState } from 'react';

import { Header, PageHeaderDashboard } from 'presentation/components/header';

import {
  GetConferenceDataDashboard,
  GetUserDataDashboard,
} from 'domain/usecases/dashboard/remote';
import { makeRemoteGetConferenceDataDashboard } from 'main/factories/usecases/dashboard/GetConferenceDataDashboardFactory';
import { makeRemoteGetUserDataDashboard } from 'main/factories/usecases/dashboard/GetUserDataDashboardFactory';
import { makeRemoteGetConferenceHistoryDashboard } from 'main/factories/usecases/dashboard/GetConferenceHistoryDashboardFactory';
import { useSelector } from 'react-redux';
import { iStore } from 'domain/interfaces/models';
import { getRole } from 'utils/getRole';
import GeneralData from './generalData';
import { Body, Container } from './styles/styledDashboard';

interface dataHistory {
  time: string;
  records: number;
  notRecords: number;
  hoursRecorded: number;
}

const Dashboard: React.FC = (): JSX.Element => {
  const [org, setOrg] = useState('All');
  const [unit, setUnit] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [period, setPeriod] = useState('All');
  const [conferenceData, setConferenceData] =
    useState<GetConferenceDataDashboard.Model>({});
  const [userData, setUserData] = useState<GetUserDataDashboard.Model>({});
  const [conferenceHistory, setConferenceHistory] = useState<dataHistory[]>([]);

  const { user } = useSelector((store: iStore) => store.auth);

  const role = useMemo(
    () =>
      user?.administrator
        ? getRole(user.administrator, user.org.id, user.orgUnit.id)
        : 'STANDARD',
    [user?.administrator, user?.org.id, user?.orgUnit.id],
  );

  const verifyRole = () => {
    if (role === 'ORG_ADMIN') {
      return {
        org: user?.org?.id,
      };
    }
    if (role === 'UNIT_ADMIN') {
      return {
        org: user?.org?.id,
        unit: user?.orgUnit?.id,
      };
    }
    return {};
  };

  const getScale = (p: string) => {
    if (p === 'All' || p === 'lastYear') return 'month';
    return p === 'lastMonth' ? 'day' : 'hour';
  };

  useEffect(() => {
    const until =
      period === 'today'
        ? `${dateFns.format(new Date(), 'yyyy-MM-dd')}T23:59`
        : dateFns.format(new Date(), 'yyyy-MM-dd');

    makeRemoteGetConferenceDataDashboard()
      .getConferenceData({
        query: {
          ...(org !== 'All' ? { org: Number(org) } : verifyRole()),
          ...(unit !== 'All' ? { unit: Number(unit) } : verifyRole()),
          until,
          ...(dateFrom !== '' ? { from: dateFrom } : {}),
        },
      })
      .then(res => {
        setConferenceData(res);
      });

    makeRemoteGetUserDataDashboard()
      .getUserData({
        query: {
          ...(org !== 'All' ? { org: Number(org) } : verifyRole()),
          ...(unit !== 'All' ? { unit: Number(unit) } : verifyRole()),
          until,
          ...(dateFrom !== '' ? { from: dateFrom } : {}),
          disablePagination: true,
        },
      })
      .then(res => {
        setUserData(res);
      });

    makeRemoteGetConferenceHistoryDashboard()
      .getConferenceHistory({
        query: {
          ...(org !== 'All' ? { org: Number(org) } : verifyRole()),
          ...(unit !== 'All' ? { unit: Number(unit) } : verifyRole()),
          scale: getScale(period),
          until,
          ...(dateFrom !== '' ? { from: dateFrom } : {}),
        },
      })
      .then(res => {
        const auxConferenceHistory: dataHistory[] = [];

        if (res.history !== undefined) {
          const data = res.history[0];
          data.xAxis.forEach((item, index) => {
            let time = item;

            if (getScale(period) === 'month')
              time = dateFns.format(new Date(item), 'MMM', {
                locale: ptBR,
              });
            else if (getScale(period) === 'day')
              time = dateFns.format(new Date(item), 'dd/MM');
            else if (getScale(period) === 'hour')
              time = dateFns.format(new Date(item), 'HH:mm');

            auxConferenceHistory.push({
              time,
              records: data.yAxis[0].data[index],
              notRecords: data.yAxis[1].data[index],
              hoursRecorded: data.yAxis[2].data[index],
            });
          });
        }

        setConferenceHistory(auxConferenceHistory);
      });
  }, [org, unit, dateFrom, period]);

  return (
    <Container>
      <div style={{ position: 'fixed', width: '100%', zIndex: 2 }}>
        <Header title="teste" />
        <PageHeaderDashboard
          org={org}
          setOrg={setOrg}
          unit={unit}
          setUnit={setUnit}
          period={period}
          setPeriod={setPeriod}
          dateFrom={dateFrom}
          setDateFrom={setDateFrom}
        />
      </div>
      <Body style={{ marginTop: '152px' }}>
        <GeneralData
          conferenceData={conferenceData}
          conferenceHistory={conferenceHistory}
        />
      </Body>
    </Container>
  );
};

export default Dashboard;
